import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { NotificationsDropdown } from './NotificationsDropdown';
import { Dice6, Library, PlayCircle, Users, LogOut, Bug, Menu, X, UserCircle, Calendar, Settings, UserPlus, Trello, LucideIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBugReport } from '../contexts/BugReportContext';

interface NavGroupProps {
  children: ReactNode;
  title?: string;
  className?: string;
}

interface NavLinkProps {
  to?: string;
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  badge?: ReactNode;
}

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        menuButton.current?.focus();
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isOpen]);
  const auth = useAuth();
  const { currentUser, isAdmin } = auth;
  const navigate = useNavigate();
  const { reportBug } = useBugReport();

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  const NavGroup = ({ children, title, className = "" }: NavGroupProps) => (
    <div className={`flex flex-col min-[1920px]:flex-row min-[1920px]:items-center min-[1920px]:space-x-4 ${className}`}>
      {title && (
        <div className="px-4 py-2 text-xs font-semibold text-brand-gray-500 bg-brand-gray-50 min-[1920px]:hidden">
          {title}
        </div>
      )}
      <div className="flex flex-col min-[1920px]:flex-row min-[1920px]:items-center min-[1920px]:space-x-4">
        {children}
      </div>
    </div>
  );

  const NavLink = ({ to, icon: Icon, label, onClick, className = "", disabled = false, badge = null }: NavLinkProps) => {
    const baseClass = "nav-link gap-3 p-4 flex items-center min-[1920px]:px-2 min-[1920px]:py-2 min-[1920px]:gap-2 relative transition-colors duration-150 whitespace-nowrap";
    const activeClass = "bg-brand-blue-50 text-brand-blue-600 min-[1920px]:bg-transparent min-[1920px]:text-brand-blue-600";
    const defaultClass = disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-brand-gray-50 min-[1920px]:hover:bg-transparent min-[1920px]:hover:text-brand-blue-600";
    
    const content = (
      <>
        <Icon className="h-5 w-5" />
        <span>{label}</span>
        {badge}
      </>
    );

    if (disabled) {
      return (
        <div className={`${baseClass} ${defaultClass} ${className}`}>
          {content}
        </div>
      );
    }

    return to ? (
      <Link
        to={to}
        className={`${baseClass} ${defaultClass} ${className}`}
        onClick={onClick}
        aria-label={label}
      >
        {content}
      </Link>
    ) : (
      <button
        onClick={onClick}
        className={`${baseClass} ${defaultClass} w-full text-left ${className}`}
        aria-label={label}
      >
        {content}
      </button>
    );
  };

  const NavLinks = () => (
    <>
      {/* Core Features Group */}
      <NavGroup title="Core Features" className="min-[1920px]:pr-4 min-[1920px]:border-r border-brand-gray-200">
        <NavLink
          to="/my-games"
          icon={Library}
          label="My Games"
          onClick={() => setIsOpen(false)}
          className="text-brand-blue-600"
          data-tutorial="my-games-link"
        />
        <NavLink
          to="/borrow"
          icon={PlayCircle}
          label="Borrow Games"
          onClick={() => setIsOpen(false)}
          className="text-brand-blue-600"
          data-tutorial="borrow-link"
        />
      </NavGroup>

      {/* Social Features Group */}
      <NavGroup title="Social" className="border-t min-[1920px]:border-t-0 min-[1920px]:px-4 min-[1920px]:border-r border-brand-gray-200">
        <NavLink
          to="/friends"
          icon={UserPlus}
          label="Friends"
          onClick={() => setIsOpen(false)}
        />
        <NavLink
          to="/game-nights"
          icon={Calendar}
          label="Game Nights"
          onClick={() => setIsOpen(false)}
        />
      </NavGroup>

      {/* User Features Group */}
      <NavGroup title="User" className="border-t min-[1920px]:border-t-0 min-[1920px]:px-4 min-[1920px]:border-r border-brand-gray-200">
        <NavLink
          to="/profile"
          icon={UserCircle}
          label="Profile"
          onClick={() => setIsOpen(false)}
        />
        <div className="nav-link gap-3 p-4 flex items-center min-[1920px]:p-2 min-[1920px]:gap-2">
          <NotificationsDropdown />
          <span className="min-[1920px]:hidden">Notifications</span>
        </div>
      </NavGroup>

      {/* System Features Group */}
      <NavGroup title="System" className="border-t min-[1920px]:border-t-0 min-[1920px]:pl-4">
        <NavLink
          to="/roadmap"
          icon={Trello}
          label="Feature Roadmap"
          onClick={() => setIsOpen(false)}
        />
        {isAdmin && (
          <NavLink
            to="/admin"
            icon={Settings}
            label="Admin Dashboard"
            onClick={() => setIsOpen(false)}
          />
        )}
        <NavLink
          icon={Bug}
          label="Report Bug"
          onClick={() => {
            reportBug();
            setIsOpen(false);
          }}
          className="text-brand-gray-500"
          to="#"
        />
        <NavLink
          icon={LogOut}
          label="Sign Out"
          onClick={() => {
            handleSignOut();
            setIsOpen(false);
          }}
          className="text-brand-gray-500"
          to="#"
        />
      </NavGroup>
    </>
  );

  return (
    <nav className="bg-white border-b border-brand-gray-200 relative z-30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-full overflow-x-hidden">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <Dice6 className="h-6 w-6 text-brand-blue-600" />
              <span className="font-display text-xl font-semibold text-brand-gray-900">BoardGameBorrow</span>
            </Link>
          </div>

          {!currentUser && (
            <div className="flex items-center">
              <Link to="/login" className="btn-secondary">
                Log In
              </Link>
            </div>
          )}

          {currentUser && (
            <>
              {/* Desktop Navigation */}
              <div className="hidden min-[1920px]:flex min-[1920px]:flex-row items-center min-[1920px]:space-x-4">
                <NavLinks />
              </div>

              {/* Mobile Menu Button */}
              <div className="min-[1920px]:hidden">
                <button
                  ref={menuButton}
                  aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
                  aria-expanded={isOpen}
                  aria-controls="main-navigation-menu"
                  onClick={() => setIsOpen(!isOpen)}
                  className="p-2 rounded-lg text-brand-gray-500 hover:bg-brand-gray-100"
                >
                  {isOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Mobile Navigation */}
        {isOpen && currentUser && (
          <div className="min-[1920px]:hidden border-t border-brand-gray-200 bg-white fixed left-0 right-0 top-16 bottom-0">
            <div className="h-full overflow-y-auto">
              <nav id="main-navigation-menu" className="flex flex-col" aria-label="Main navigation">
                <NavLinks />
              </nav>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
